import { useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import LoadingSpinner from '../../components/LoadingSpinner';
import FloatingActionButton from '../../components/FloatingActionButton';
import { useAxios, APIError } from '../../core/_axios';
import { Service } from '../../core/_models';
import { getServices } from '../../core/_requests_v2';
import usePageTitle from '../../hooks/usePageTitle';
import { useAuth } from '../../AuthContext';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

// Format date range with more readable format and age calculation
function formatDateRange(birthDate: string, deathDate: string): string {
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const date = DateTime.fromISO(dateStr);
        if (!date.isValid) return dateStr;
        
        // Format as "Month Day, Year" (e.g., "January 15, 1950")
        return date.toFormat('LLLL d, yyyy');
    };
    
    const birth = formatDate(birthDate);
    const death = formatDate(deathDate);
    
    if (!birth && !death) return '';
    if (!birth) return death;
    if (!death) return `Born ${birth}`;
    
    // Calculate age
    const birthDateTime = DateTime.fromISO(birthDate);
    const deathDateTime = DateTime.fromISO(deathDate);
    if (birthDateTime.isValid && deathDateTime.isValid) {
        const age = Math.floor(deathDateTime.diff(birthDateTime, 'years').years);
        // Only show age if greater than 0
        if (age > 0) {
            return `${birth} – ${death} (Age ${age})`;
        }
    }
    
    return `${birth} – ${death}`;
}

export function ServicesList() {
    const { copy } = useLocalizedCopy();

    const {
        viewingAllAccounts
    } = useAuth();

    const axios = useAxios();

    usePageTitle(copy.Services.list.title);

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [services, setServices] = useState<Service[]>([]);
    const [totalServices, setTotalServices] = useState<null | number>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showOnlySpanish, setShowOnlySpanish] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const shouldMaintainFocus = useRef(false);

    const itemsPerPage = 50; // Set in the backend

    // Memoized placeholder to prevent unnecessary re-renders
    const searchPlaceholder = useMemo(() => {
        return totalServices ? `Search ${totalServices.toLocaleString()} services...` : 'Search services...';
    }, [totalServices]);

    const loadServices = useCallback(async (loadPage: number = 1) => {
        const isLoadingMore = loadPage > 1;

        if (isLoadingMore) {
            setLoadingMore(true);
            setCurrentPage(loadPage);
        } else {
            setLoading(true);
            setServices([]); // Reset services if it's the first page
        }

        setLoadingError(null);

        try {
            const {
                services: updatedServices,
                total_services: updatedTotalServices
            } = await getServices(axios, loadPage, showOnlySpanish ? 'es-ES' : undefined, searchQuery); // Pass language and search filters to API

            setServices((prevServices) =>
                isLoadingMore
                    ? [...prevServices, ...updatedServices]  // Append on loading more
                    : updatedServices // Reset on first load
            );

            setTotalServices(updatedTotalServices);
            setHasMore(updatedServices.length === itemsPerPage);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : `${copy.Common.messages.errorOccurred}. ${copy.Common.messages.tryAgain}.`;
            setLoadingError(errorMessage);
        } finally {
            if (isLoadingMore) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, [axios, showOnlySpanish, searchQuery]);

    // No more client-side filtering - backend handles it
    const filteredServices = services;

    useEffect(() => {
        loadServices(1); // Reset to first page when filter changes
    }, [loadServices]);

    // Reset page when search changes
    useEffect(() => {
        if (searchQuery !== '') { // Only reload when search actually changes
            setCurrentPage(1);
        }
    }, [searchQuery]);
    
    // Restore focus after DOM updates
    useLayoutEffect(() => {
        if (shouldMaintainFocus.current && searchInputRef.current && !loading) {
            searchInputRef.current.focus();
            shouldMaintainFocus.current = false;
        }
    }, [loading, services]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const handleExportCSV = () => {
        const headers = ['Service Name', 'Date of Birth', 'Date of Death', 'Age', 'Service Date', 'Language', 'Created'];
        const rows = filteredServices.map(service => {
            const birthDate = service.public_data.deceased_person.birth_date || '';
            const deathDate = service.public_data.deceased_person.death_date || '';
            const age = birthDate && deathDate ? 
                Math.floor(DateTime.fromISO(deathDate).diff(DateTime.fromISO(birthDate), 'years').years) : '';
            
            const lang = (service.public_data as any).language || 'en-US';
            const eventDate = (service.public_data as any).event_date || '';
            
            return [
                service.public_data.deceased_person.full_name,
                birthDate ? DateTime.fromISO(birthDate).toFormat('MM/dd/yyyy') : '',
                deathDate ? DateTime.fromISO(deathDate).toFormat('MM/dd/yyyy') : '',
                age,
                eventDate ? DateTime.fromISO(eventDate).toFormat('MM/dd/yyyy') : '',
                lang === 'es-ES' ? 'Spanish' : 'English',
                DateTime.fromISO(service.inserted_at).toFormat('MM/dd/yyyy')
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `services_${DateTime.now().toFormat('yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        loadServices();
                    }}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </>
        );
    }

    return <>
        <div className="grid gap-5 lg:gap-7.5">
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-3">
                <div className="relative flex-1 max-w-md">
                    <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"></i>
                    <input
                        ref={searchInputRef}
                        id="services-search-input"
                        key="services-search-input"
                        type="text"
                        className="input input-sm pl-8 w-full"
                        placeholder={searchPlaceholder}
                        value={searchInput}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        onChange={(e) => {
                            const value = e.target.value;
                            setSearchInput(value);
                            
                            // Only track focus if input is currently focused (user is actively typing)
                            if (document.activeElement === searchInputRef.current) {
                                shouldMaintainFocus.current = true;
                            }
                            
                            // Debounce search
                            if (debounceTimer.current) {
                                clearTimeout(debounceTimer.current);
                            }
                            
                            debounceTimer.current = setTimeout(() => {
                                setSearchQuery(value);
                            }, 300);
                        }}
                    />
                    {searchInput && (
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                                // Clear the debounced search timer first
                                if (debounceTimer.current) {
                                    clearTimeout(debounceTimer.current);
                                }
                                
                                setSearchQuery('');
                                setSearchInput('');
                                
                                // Set flag to restore focus after the API call and re-render
                                shouldMaintainFocus.current = true;
                            }}
                        >
                            <i className="ki-filled ki-cross-circle"></i>
                        </button>
                    )}
                </div>
                <div className="flex gap-3 items-center">
                    <label className="switch switch-sm">
                        <input 
                            className="order-2" 
                            name="spanishFilter" 
                            type="checkbox" 
                            checked={showOnlySpanish}
                            onChange={(e) => {
                                setShowOnlySpanish(e.target.checked);
                                setCurrentPage(1); // Reset to first page when filter changes
                            }}
                        />
                        <span className="switch-label order-1">
                            Only Spanish
                        </span>
                    </label>
                    <button className="btn btn-sm btn-light" onClick={handleExportCSV}>
                        <i className="ki-filled ki-file-down text-sm me-1"></i>
                        Export CSV
                    </button>
                    {!viewingAllAccounts && (
                        <Link className="btn btn-sm btn-primary" to="/create-service">
                            <i className="ki-filled ki-plus-squared text-sm me-1"></i>
                            {copy.CreateService.title}
                        </Link>
                    )}
                </div>
            </div>
            <div className="card-body">
                <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                    <div className="scrollable-x-auto">
                        <table className="table table-fixed table-border" data-datatable-table="true">
                            {filteredServices.length > 0 && <thead>
                                <tr>
                                    <th className="w-[350px]">
                                        <span className="sort asc">
                                            <span className="sort-label">
                                                {copy.Services.view.deceasedName}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    {viewingAllAccounts && <th>
                                        {copy.Common.navigation.account}
                                    </th>}
                                    <th className="w-[200px]">
                                        <span className="sort">
                                            <span className="sort-label">
                                                {copy.Services.list.created}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                </tr>
                            </thead>}
                            {filteredServices.length > 0 ? <tbody>
                                {filteredServices.map((service) => {
                                    const isSpanish = service.public_data.current_language === 'es-ES';
                                    return <tr key={service.unique_identifier}>
                                        <td>
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex flex-row items-center gap-2">
                                                    {service.public_data.deceased_person.avatar_url && (
                                                        <img 
                                                            src={service.public_data.deceased_person.avatar_url}
                                                            alt={service.public_data.deceased_person.full_name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                            onError={(e) => {
                                                                // Hide image if it fails to load
                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                            }}
                                                        />
                                                    )}
                                                    <Link
                                                        to={`/services/${service.unique_identifier}`}
                                                        className="leading-none font-semibold text-sm text-gray-900 hover:text-primary"
                                                    >
                                                        {service.public_data.deceased_person.full_name}
                                                    </Link>
                                                    <span 
                                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                            isSpanish 
                                                                ? 'bg-green-100 text-green-800' 
                                                                : 'bg-blue-100 text-blue-800'
                                                        }`}
                                                    >
                                                        {isSpanish ? 'ES' : 'EN'}
                                                    </span>
                                                </div>
                                                <span className="text-2sm text-gray-600">
                                                    {formatDateRange(service.public_data.deceased_person.birth_date, service.public_data.deceased_person.death_date)}
                                                </span>
                                            </div>
                                        </td>
                                        {viewingAllAccounts && <td>
                                            {service.account.name}
                                        </td>}
                                        <td>
                                            {DateTime.fromISO(service.inserted_at).toRelative()}
                                        </td>
                                    </tr>
                                })}
                            </tbody> : <tbody>
                                <tr>
                                    <td colSpan={viewingAllAccounts ? 3 : 2} className="text-center">
                                        <div>
                                            {showOnlySpanish && services.length > 0 
                                                ? 'No Spanish services found' 
                                                : copy.Services.list.noServices}
                                        </div>
                                        <div className="mt-3">
                                            {!viewingAllAccounts && !showOnlySpanish && <Link className="btn btn-sm btn-light" to="/create-service">
                                                {copy.Services.list.createService}
                                            </Link>}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>}
                        </table>
                    </div>
                </div>
            </div>
            {hasMore && !searchQuery && <div className="card-footer">
                <div className="flex justify-center">
                    <button
                        disabled={loadingMore}
                        className="btn btn-lg btn-light"
                        onClick={() => {
                            loadServices(currentPage + 1);
                        }}
                    >
                        {copy.Pagination.next}
                    </button>
                </div>
            </div>}
            </div>
        </div>
        
        {/* Floating Action Button for mobile */}
        {!viewingAllAccounts && (
            <FloatingActionButton 
                to="/create-service"
                icon="ki-plus"
                label={copy.CreateService.title}
            />
        )}
    </>
}